import React, { useState, useEffect, useRef } from 'react';
import { getCustomers } from '../../services/customerService';
import { getStores } from '../../services/storeService';
import '../../css/TargetSelector.css';

/**
 * 쿠폰 발급 대상 검색-선택 컴포넌트
 *
 * @param {'CUSTOMER'|'STORE'} type
 * @param {Array<{id: string, label: string, sub?: string}>} value 선택된 항목
 * @param {(items: Array) => void} onChange
 * @param {boolean} disabled
 */
const TargetSelector = ({ type, value = [], onChange, disabled = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const placeholder = type === 'CUSTOMER'
    ? '고객 닉네임, 전화번호로 검색'
    : '매장명, 주소로 검색';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 디바운스 검색
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        if (type === 'CUSTOMER') {
          const data = await getCustomers({ search: query.trim(), size: 10 });
          const content = data.content || [];
          setResults(content.map((c) => ({
            id: c.customer_id,
            label: c.nickname || '(닉네임 없음)',
            sub: c.phone || c.social_id || '',
          })));
        } else {
          const data = await getStores({ search: query.trim(), size: 10 });
          const stores = data.stores || data.content || [];
          setResults(stores.map((s) => ({
            id: s.store_id,
            label: s.store_name || s.name || '(이름 없음)',
            sub: s.address || '',
          })));
        }
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, type]);

  const isSelected = (id) => value.some((v) => v.id === id);

  const handleSelect = (item) => {
    if (isSelected(item.id)) return;
    onChange([...value, item]);
    setQuery('');
    setResults([]);
  };

  const handleRemove = (id) => {
    onChange(value.filter((v) => v.id !== id));
  };

  return (
    <div className="target-selector" ref={containerRef}>
      <div className="target-chips">
        {value.length === 0 && (
          <span className="target-empty">{type === 'CUSTOMER' ? '대상 고객 미선택' : '대상 매장 미선택'}</span>
        )}
        {value.map((item) => (
          <span key={item.id} className="target-chip">
            {item.label}
            {!disabled && (
              <button type="button" className="chip-remove" onClick={() => handleRemove(item.id)} aria-label="제거">×</button>
            )}
          </span>
        ))}
      </div>

      <div className="target-search">
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
        />
        {open && (query.trim() || loading) && (
          <div className="target-results">
            {loading ? (
              <div className="target-result-empty">검색 중...</div>
            ) : results.length === 0 ? (
              <div className="target-result-empty">결과 없음</div>
            ) : (
              results.map((item) => (
                <div
                  key={item.id}
                  className={`target-result-item ${isSelected(item.id) ? 'selected' : ''}`}
                  onClick={() => handleSelect(item)}
                >
                  <div className="target-result-label">{item.label}</div>
                  {item.sub && <div className="target-result-sub">{item.sub}</div>}
                  {isSelected(item.id) && <span className="target-result-check">✓</span>}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TargetSelector;